import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Currency } from '@/components/common/Currency';
import type { ArInvoicePaymentApplication } from '@erp/rpc';
import { T } from 'gt-react';

/** US short date for AR tables (avoids i18n lint on format() pattern literal) */
const DATE_FMT_US = 'MM/dd/yyyy';

interface ARInvoicePaymentHistoryProps {
  paymentApplications: ArInvoicePaymentApplication[];
  currency?: string | null;
}

function toTitleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function ARInvoicePaymentHistory({
  paymentApplications,
  currency,
}: ARInvoicePaymentHistoryProps) {
  const navigate = useNavigate();
  const currencyCode = currency ?? 'USD';

  if (paymentApplications.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        <T context="AR invoice detail, empty payment applications list">
          No payments recorded
        </T>
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <T context="AR invoice payment history column, payment reference">
              Reference #
            </T>
          </TableHead>
          <TableHead>
            <T context="AR invoice payment history column, date payment was made">
              Payment Date
            </T>
          </TableHead>
          <TableHead className="text-right">
            <T context="AR invoice payment history column, amount applied to invoice">
              Applied Amount
            </T>
          </TableHead>
          <TableHead>
            <T context="AR invoice payment history column, payment method">
              Method
            </T>
          </TableHead>
          <TableHead>
            <T context="AR invoice payment history column, when payment was applied">
              Applied On
            </T>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {paymentApplications.map((app) => {
          const paymentDate = app.payment?.paymentDate
            ? parseDateOnly(app.payment.paymentDate)
            : null;
          const applicationDate = app.applicationDate
            ? parseDateOnly(app.applicationDate)
            : null;

          return (
            <TableRow key={app.id}>
              <TableCell>
                {app.payment?.id ? (
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline cursor-pointer font-medium"
                    onClick={() => {
                      if (!app.payment?.id) return;
                      navigate({
                        to: '/accounts-receivable/payments/$arPaymentId',
                        params: { arPaymentId: app.payment.id },
                        viewTransition: true,
                      });
                    }}
                  >
                    {app.payment.referenceNumber ?? app.payment.id.slice(0, 8)}
                  </button>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    <T context="empty value placeholder in table">—</T>
                  </span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {paymentDate ? format(paymentDate, DATE_FMT_US) : '—'}
              </TableCell>
              <TableCell className="text-right text-sm font-semibold">
                <Currency
                  value={app.appliedAmount}
                  currency={currencyCode}
                  fallback="—"
                />
              </TableCell>
              <TableCell className="text-sm">
                {app.payment?.paymentMethod
                  ? toTitleCase(app.payment.paymentMethod)
                  : '—'}
              </TableCell>
              <TableCell className="text-sm">
                {applicationDate ? format(applicationDate, DATE_FMT_US) : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
